<template>
	<div class="eli-suggestion-button" :class="classAddons">
		<SuiButton
			class="eli-suggestion-button__primary"
			variant="secondary"
			size="medium"
			:aria-label="primaryButtonLabel"
			:disabled="isInteractionDisabled"
			:loading="loading"
			@click="handlePrimaryClick"
		>
			<img
				class="eli-suggestion-button__head"
				:src="ELI_AVATAR_ROBOT"
				alt=""
				aria-hidden="true"
			/>
			<span class="eli-suggestion-button__prefix">{{ buttonLabel }}</span>
		</SuiButton>

		<AppDropdown
			v-if="showSecondarySuggestions"
			:trigger-button-props="menuTriggerButtonProps"
			:popover-props="menuPopoverProps"
			@show="handleMenuOpen"
		>
			<template #trigger>
				<span class="eli-suggestion-button__menu-trigger-content">
					<SuiIcon name="icon-meatballs-16" />
				</span>
			</template>

			<template #default="{ closeDropdown, isOpen }">
				<div
					v-if="isOpen"
					v-close-popper
					class="eli-suggestion-button__menu"
					role="menu"
					:aria-label="$t('eli_suggestion_button_menu_label')"
					tabindex="-1"
				>
					<SuiListItem
						v-for="suggestion in secondarySuggestions"
						:key="suggestion.id"
						:title="suggestion.label"
						:icon="{ name: suggestion.icon ?? 'icon-chat-16' }"
						role="menuitem"
						:aria-label="suggestion.label"
						@click="
							handleSecondarySuggestionSelect(
								suggestion,
								closeDropdown
							)
						"
					/>
				</div>
			</template>
		</AppDropdown>
	</div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { SuiButton, SuiIcon, SuiListItem } from '@developers/scoro-ui';
import { AppDropdown } from '@/features/App';
import { useTranslation } from '@/composables/useTranslation';
import type { EliSuggestion, EliSuggestionButtonPlacementType } from './types';
import { ELI_AVATAR_ROBOT } from '@/features/Eli/constants';
import { EliSuggestionButtonPlacement } from '@/features/Eli/components/EliSuggestionButton/constants';

export interface EliSuggestionButtonProps {
	buttonLabel: string;
	accessibilityLabel?: string;
	suggestions: EliSuggestion[];
	primarySuggestionId?: EliSuggestion['id'];
	disabled?: boolean;
	loading?: boolean;
	placement?: EliSuggestionButtonPlacementType;
}

const {
	buttonLabel,
	accessibilityLabel = undefined,
	suggestions,
	primarySuggestionId = undefined,
	disabled = false,
	loading = false,
	placement = EliSuggestionButtonPlacement.Default,
} = defineProps<EliSuggestionButtonProps>();

const { t } = useTranslation();

const emit = defineEmits<{
	(e: 'select', suggestion: EliSuggestion): void;
	(e: 'menu-open'): void;
}>();

const selectableSuggestions = computed(() => {
	return suggestions.filter(({ disabled }) => !disabled);
});

const primarySuggestion = computed(() => {
	return (
		selectableSuggestions.value.find(
			({ id }) => id === primarySuggestionId
		) ?? selectableSuggestions.value[0]
	);
});

const secondarySuggestions = computed(() => {
	return selectableSuggestions.value.filter(
		({ id }) => id !== primarySuggestion.value?.id
	);
});

const isInteractionDisabled = computed(() => {
	return disabled || loading || !primarySuggestion.value;
});

const showSecondarySuggestions = computed(() => {
	return (
		!loading &&
		suggestions.length > 1 &&
		(isInteractionDisabled.value || secondarySuggestions.value.length > 0)
	);
});

const primaryButtonLabel = computed(() => {
	return accessibilityLabel ?? buttonLabel;
});

const menuTriggerButtonProps = computed(() => ({
	'aria-label': t('eli_suggestion_button_more_suggestions'),
	class: 'eli-suggestion-button__menu-trigger',
	disabled: isInteractionDisabled.value,
}));

const menuPopoverProps = computed(() => ({
	distance: 0,
	placement: 'bottom-end',
}));

const classAddons = computed(() => ({
	'eli-suggestion-button--compact':
		placement === EliSuggestionButtonPlacement.Compact,
	'eli-suggestion-button--disabled': isInteractionDisabled.value,
	'eli-suggestion-button--single-action': !showSecondarySuggestions.value,
}));

const handlePrimaryClick = () => {
	if (!primarySuggestion.value || isInteractionDisabled.value) {
		return;
	}

	handleSuggestionSelect(primarySuggestion.value);
};

const handleMenuOpen = () => {
	if (isInteractionDisabled.value) {
		return;
	}

	emit('menu-open');
};

const handleSuggestionSelect = (suggestion: EliSuggestion) => {
	emit('select', suggestion);
};

const handleSecondarySuggestionSelect = (
	suggestion: EliSuggestion,
	closeDropdown: () => void
) => {
	handleSuggestionSelect(suggestion);
	closeDropdown();
};
</script>

<style scoped lang="scss">
.eli-suggestion-button {
	align-items: center;
	height: 4rem;
	display: flex;
	background: linear-gradient(
		268deg,
		#{$purple-20} 0%,
		#{$purple-10} 40%,
		#{$purple-10} 85%,
		#{$purple-30} 100%
	);
	border-radius: 2rem 0.5rem 0.5rem 2rem;
	overflow: hidden;

	&__primary {
		--sui-button-hover-background-color: #{$purple-20};
		--sui-button-text-color: #{$purple-70};
		--sui-button-background-color: transparent;

		transition: background-color 0.1s ease;
		gap: 1rem;
		padding: 0.5rem 1.5rem 0.5rem 0.5rem;
	}

	&__head {
		display: block;
		width: 3rem;
		height: 3rem;
		flex-shrink: 0;
		align-self: center;
		border-radius: 50%;
		object-fit: cover;
	}

	&__menu-trigger-content {
		height: 4rem;
		width: 4rem;
		border-left: 1px solid $purple-30;

		--sui-icon-fill: #{$purple-60};

		display: flex;
		align-items: center;
		justify-content: center;
		transition: background-color 0.1s ease;

		&:hover,
		&:focus-visible {
			--sui-icon-fill: #{$purple-70};

			background: $purple-30;
		}
	}

	&__menu {
		--sui-list-item-title-size: 1.5rem;
		--sui-icon-fill: #{$neutral-60};

		background-color: $neutral-00;
	}

	&--compact {
		.eli-suggestion-button__prefix {
			display: none;
		}
	}

	&--disabled {
		opacity: 0.6;
	}
}
</style>
